"use client";

import { usePathname } from "next/navigation";
import { useEffect } from "react";
import { trackEvent } from "@/lib/analytics/events";
import { promotionParams, teaserFromDataset } from "@/lib/analytics/teaser";

const SELECTOR = "[data-sponsored-teaser]";
// The IAB viewability rule for display: half the element, one second.
const VISIBLE_RATIO = 0.5;
const VISIBLE_MS = 1000;

/**
 * Impressions and clicks of the sponsored cards on the page — the rules are in
 * lib/analytics/teaser.ts. Mounted with GA4 (GoogleAnalytics.tsx). Each chip
 * counts once per page view: the path is part of the effect, so an App Router
 * navigation starts a fresh count, and cards that render later (a list that
 * streams in, "load more") are picked up as they appear.
 */
export function SponsoredTeasers() {
  const path = usePathname() ?? "";

  useEffect(() => {
    const seen = new WeakSet<Element>();
    const timers = new Map<Element, ReturnType<typeof setTimeout>>();

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const el = e.target as HTMLElement;
          if (e.intersectionRatio >= VISIBLE_RATIO && !seen.has(el)) {
            if (timers.has(el)) continue;
            timers.set(
              el,
              setTimeout(() => {
                timers.delete(el);
                const t = teaserFromDataset(el.dataset);
                if (t && trackEvent("view_promotion", promotionParams(t, path))) {
                  seen.add(el);
                  io.unobserve(el);
                }
              }, VISIBLE_MS),
            );
          } else {
            clearTimeout(timers.get(el));
            timers.delete(el);
          }
        }
      },
      { threshold: [0, VISIBLE_RATIO] },
    );

    const watch = (root: ParentNode) => {
      for (const el of root.querySelectorAll(SELECTOR)) if (!seen.has(el)) io.observe(el);
    };
    watch(document);
    const mo = new MutationObserver((records) => {
      for (const r of records)
        for (const n of r.addedNodes) {
          if (!(n instanceof Element)) continue;
          if (n.matches(SELECTOR)) io.observe(n);
          watch(n);
        }
    });
    mo.observe(document.body, { childList: true, subtree: true });

    // A click on the card's link to the item: the link and the chip share the
    // card (its list item or article), so a link elsewhere on the page to the
    // same item is not counted as the teaser's click.
    const onClick = (e: MouseEvent) => {
      const a = e.target instanceof Element ? e.target.closest<HTMLAnchorElement>("a[href]") : null;
      if (!a) return;
      const card = a.closest("li, article") ?? a;
      const href = a.getAttribute("href");
      for (const el of card.querySelectorAll<HTMLElement>(SELECTOR)) {
        const t = teaserFromDataset(el.dataset);
        if (t && t.href === href) {
          trackEvent("select_promotion", promotionParams(t, path));
          return;
        }
      }
    };
    document.addEventListener("click", onClick, { capture: true });

    return () => {
      io.disconnect();
      mo.disconnect();
      for (const t of timers.values()) clearTimeout(t);
      document.removeEventListener("click", onClick, { capture: true });
    };
  }, [path]);

  return null;
}
